import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { FormGroup, Input } from 'reactstrap'
import styled from 'styled-components'
import _ from 'lodash'

import { Show } from '~/components/commons'
import EditableRow from '~/components/EditableRow'
import Checkbox from '~/components/Checkbox'
import Select from '~/components/Select'
import { queries } from '~/constants/mediaQuery'
import { Check } from 'react-feather'
import useMultiPropState from '~/hooks/useMultiPropState'

function WebsiteField({
  controller,
  name,
  label,
  onSwitchChange,
  editableFields,
  initialUrl = '',
}) {
  const switchId = `${name}Switch`
  const baseInputRef = useRef(null)
  const [tracks, setTracks] = useState(false)
  const [inputs, handleInputChange, setInputs] = useMultiPropState({
    source: '',
    medium: '',
    campaign: '',
  })
  const [baseUrl, setBaseUrl] = useState('')

  const handleBaseUrlChange = useRef(
    _.debounce(value => {
      setBaseUrl(value)
    }, 250)
  ).current

  const setControllerInput = useCallback(
    _.debounce((name, value) => controller.setFieldValue(name, value), 250),
    []
  )

  useEffect(() => {
    if (!baseUrl) {
      setControllerInput(name, '')
      return
    }

    try {
      const url = new URL(baseUrl)

      Object.entries(inputs)
        .filter(([, value]) => !!value)
        .forEach(([key, value]) => {
          url.searchParams.append(`utm_${key}`, value)
        })

      // Old URL building logic
      // const isDomainOnly = baseUrl => {
      //   const re = /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/g
      //   const matchStr = baseUrl.match(re)
      //   return matchStr?.[1]?.split('/').length <= 1
      // }

      // const buildFullUrl = (baseUrl, params = '') => {
      //   if (isDomainOnly(baseUrl)) {
      //     if (baseUrl?.endsWith('/')) {
      //       return baseUrl && !!params ? `${baseUrl}?${params}` : baseUrl
      //     } else {
      //       return baseUrl && !!params ? `${baseUrl}/?${params}` : baseUrl
      //     }
      //   } else {
      //     return baseUrl && !!params ? `${baseUrl}?${params}` : baseUrl
      //   }
      // }

      // const params = Object.entries(inputs)
      //   .filter(([, value]) => !!value)
      //   .map(([key, value]) => `utm_${key}=${value}`)
      //   .join('&')

      // const fullUrl = buildFullUrl(baseUrl, params)
      setControllerInput(name, url.toString())
    } catch (error) {
      setControllerInput(name, baseUrl)
    }
  }, [baseUrl, inputs, name, setControllerInput])

  useEffect(() => {
    if (!initialUrl) return
    const [baseUrl, params] = initialUrl.split('?')
    const queryParams = new URLSearchParams(params)
    baseInputRef.current.value = baseUrl
    const {
      utm_source: source = '',
      utm_medium: medium = '',
      utm_campaign: campaign = '',
    } = Object.fromEntries(queryParams.entries())
    setTracks(true)
    setInputs({ source, medium, campaign })
  }, [initialUrl, setInputs, setTracks])

  const campaignOptions = [
    { label: 'locasty_{{store_code}}', value: 'locasty_{{store_code}}' },
    { label: 'locasty_{{local_city}}', value: 'locasty_{{local_city}}' },
    { label: 'locasty_{{local_area}}', value: 'locasty_{{local_area}}' },
  ]
  return (
    <>
      <EditableRow
        switchId={switchId}
        onChange={() => {
          if (editableFields?.[switchId]) {
            if (!initialUrl) {
              setBaseUrl('')
              baseInputRef.current.value = ''
              setInputs({})
              setTracks(false)
            } else {
              const [baseUrl] = initialUrl.split('?')
              setBaseUrl(baseUrl)
              baseInputRef.current.value = baseUrl
            }
          }
          onSwitchChange(switchId)
        }}
        checkboxStyle={{
          alignSelf: 'start',
          marginTop: '27px',
        }}
        isChecked={!!editableFields?.[switchId]}
      >
        <FormGroup className={`mb-${editableFields?.[switchId] ? '0' : '1'}`}>
          <label htmlFor={name}>
            <FormattedMessage id={label} />:
          </label>
          <Input
            innerRef={baseInputRef}
            id={name}
            name={name}
            disabled={!editableFields?.[switchId]}
            onChange={e => handleBaseUrlChange(e.target.value)}
          />
        </FormGroup>
      </EditableRow>
      <Show condition={!!editableFields?.[switchId]}>
        <div className="mb-1">
          <Checkbox
            color="primary"
            label="Tracking this click on results"
            size="sm"
            icon={<Check className="vx-icon" size={10} />}
            checked={tracks}
            onChange={e => {
              if (!e.target.checked) {
                if (!initialUrl) setInputs({})
              }
              setTracks(e.target.checked)
            }}
          />

          <Show condition={!!tracks}>
            <UTMConfigContainer>
              <label>
                utm_source
                <Input
                  value={inputs.source}
                  onChange={e => handleInputChange('source', e.target.value)}
                />
              </label>
              <label>
                utm_medium
                <Input
                  value={inputs.medium}
                  onChange={e => handleInputChange('medium', e.target.value)}
                />
              </label>
              <label style={{ minWidth: 180 }}>
                utm_campaign
                <Select
                  options={campaignOptions}
                  value={
                    inputs.campaign
                      ? { label: inputs.campaign, value: inputs.campaign }
                      : null
                  }
                  onChange={e => handleInputChange('campaign', e.value)}
                />
              </label>
            </UTMConfigContainer>
          </Show>
        </div>
      </Show>
    </>
  )
}

const UTMConfigContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  width: 78%;

  label {
    font-size: 14px;

    input {
      font-size: 11px;
    }
  }

  @media ${queries.less.xll} {
    flex-direction: column;

    label {
      width: 100%;
    }
  }
`

export default WebsiteField
